import React from "react";

// Mountain / triangle logo. Yellow gradient version on light backgrounds,
// white version when placed over the purple hero gradient.
export const Logo = ({ className = "", light = false }) => (
  <svg viewBox="0 0 64 56" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="aaaLogoGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#f9e14b" />
        <stop offset="1" stopColor="#e9b208" />
      </linearGradient>
    </defs>
    <g fill={light ? "#ffffff" : "url(#aaaLogoGrad)"}>
      <path d="M2 54 L18 6 L30 30 L22 54 Z" opacity="0.95" />
      <path d="M22 54 L34 14 L46 40 L40 54 Z" opacity="0.85" />
      <path d="M40 54 L50 20 L62 54 Z" opacity="0.75" />
    </g>
  </svg>
);

// Isometric “smart city” illustration for the hero (purple / pink / white).
export const HeroCity = ({ className = "" }) => (
  <svg viewBox="0 0 520 460" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hcBase" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ef8fa4" />
        <stop offset="1" stopColor="#d8698c" />
      </linearGradient>
      <linearGradient id="hcBldA" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="1" stopColor="#f3d9e2" />
      </linearGradient>
      <linearGradient id="hcBldB" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f6e7ee" />
        <stop offset="1" stopColor="#e6bccb" />
      </linearGradient>
    </defs>

    {/* isometric platform */}
    <g>
      <polygon points="260,300 470,180 470,235 260,355 50,235 50,180" fill="#7a4fa3" opacity="0.55" />
      <polygon points="260,245 470,125 260,5 50,125" fill="url(#hcBase)" />
    </g>

    {/* buildings */}
    <g>
      {/* tall center tower */}
      <polygon points="238,150 262,164 262,60 238,46" fill="url(#hcBldB)" />
      <polygon points="262,164 300,142 300,38 262,60" fill="url(#hcBldA)" />
      <polygon points="238,46 262,60 300,38 276,24" fill="#ffffff" />
      {[0,1,2,3,4,5].map(i => (
        <rect key={i} x={270} y={60 + i*16} width={22} height={7} fill="#e79bb0" opacity="0.7" transform="skewY(-30)" />
      ))}

      {/* left block */}
      <polygon points="150,205 178,219 178,150 150,136" fill="url(#hcBldB)" />
      <polygon points="178,219 214,199 214,130 178,150" fill="url(#hcBldA)" />
      <polygon points="150,136 178,150 214,130 186,116" fill="#ffffff" />

      {/* right block */}
      <polygon points="318,206 346,220 346,158 318,144" fill="url(#hcBldB)" />
      <polygon points="346,220 382,200 382,138 346,158" fill="url(#hcBldA)" />
      <polygon points="318,144 346,158 382,138 354,124" fill="#ffffff" />
    </g>

    {/* floating accents */}
    <g fill="#c98bbf" opacity="0.85">
      <circle cx="430" cy="70" r="12" />
      <circle cx="70" cy="120" r="9" />
    </g>
    <g stroke="#ffffff" strokeWidth="2.5" fill="none" opacity="0.9">
      <path d="M408 96 a20 20 0 0 1 34 0" />
      <path d="M416 104 a11 11 0 0 1 18 0" />
    </g>
    <rect x="96" y="58" width="34" height="26" rx="5" fill="#ffffff" opacity="0.9" />
    <path d="M96 60 l17 12 l17 -12" stroke="#d8698c" strokeWidth="2.5" fill="none" />
  </svg>
);

// Isometric stacked cubes for the primary About block.
export const AboutCubes = ({ className = "" }) => (
  <svg viewBox="0 0 360 340" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="cubeGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ead4e6" />
        <stop offset="1" stopColor="#f0c9d5" />
      </linearGradient>
    </defs>
    <g stroke="#b98ab3" strokeWidth="1.4" fill="none">
      {[
        [180, 40], [110, 90], [250, 90], [180, 140], [110, 190], [250, 190], [180, 240],
      ].map(([x, y], i) => (
        <g key={i}>
          <polygon points={`${x},${y} ${x+40},${y+22} ${x},${y+44} ${x-40},${y+22}`} fill="url(#cubeGrad)" opacity="0.9" />
          <polygon points={`${x-40},${y+22} ${x},${y+44} ${x},${y+80} ${x-40},${y+58}`} fill="#e9c7d6" opacity="0.5" />
          <polygon points={`${x},${y+44} ${x+40},${y+22} ${x+40},${y+58} ${x},${y+80}`} fill="#dcb6cf" opacity="0.4" />
        </g>
      ))}
      <path d="M180 84 L180 140 M150 112 L210 112" stroke="#cf8fb0" opacity="0.6" />
    </g>
  </svg>
);

// Node network diagram (center mountain surrounded by 4 labelled circles).
export const NetworkDiagram = ({ nodes = [], className = "" }) => {
  const positions = [
    { x: 60, y: 60 },
    { x: 300, y: 60 },
    { x: 60, y: 250 },
    { x: 300, y: 250 },
  ];
  return (
    <svg viewBox="0 0 400 360" className={className} xmlns="http://www.w3.org/2000/svg">
      <g stroke="#e3c6dd" strokeWidth="1.5">
        {positions.map((p, i) => (
          <line key={i} x1="200" y1="160" x2={p.x} y2={p.y} />
        ))}
      </g>
      <circle cx="200" cy="160" r="52" fill="#f3e6f0" opacity="0.7" />
      <g transform="translate(176,132) scale(0.75)">
        <path d="M2 54 L18 6 L30 30 L22 54 Z" fill="#c9a7d6" />
        <path d="M22 54 L34 14 L46 40 L40 54 Z" fill="#d9bfe0" />
        <path d="M40 54 L50 20 L62 54 Z" fill="#e6d2eb" />
      </g>
      {positions.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="40" fill="#ffffff" stroke="#e2a7c2" strokeWidth="1.6" />
          <text x={p.x} y={p.y + 4} textAnchor="middle" fontFamily="Rubik, sans-serif" fontSize="12" fontWeight="600" fill="#8a5e94">
            {nodes[i]}
          </text>
        </g>
      ))}
    </svg>
  );
};

// Simple phone mockup used in the contact section.
export const PhoneMock = ({ className = "" }) => (
  <svg viewBox="0 0 260 480" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="40" y="20" width="180" height="440" rx="32" fill="#ffffff" stroke="#efe7f2" strokeWidth="2" />
    <rect x="52" y="36" width="156" height="408" rx="22" fill="#fbf7fc" />
    <rect x="104" y="28" width="52" height="8" rx="4" fill="#efe7f2" />
    <g transform="translate(96,210) scale(1.1)">
      <path d="M2 54 L18 6 L30 30 L22 54 Z" fill="#d6c0b0" opacity="0.6" />
      <path d="M22 54 L34 14 L46 40 L40 54 Z" fill="#cdb49f" opacity="0.55" />
      <path d="M40 54 L50 20 L62 54 Z" fill="#c2a68e" opacity="0.5" />
    </g>
  </svg>
);
